import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { useUser } from "@clerk/clerk-react";
import { testBlueprint, createBlueprint, updateBlueprint, getUserTier, listThemes } from '@/services/api';
import { toast } from '@/hooks/useToast';
import { cn } from "@/lib/utils/general";
import { CreateImageBlueprint } from '@/types/blueprints/blueprints';
import { BuilderHeader } from './BuilderHeader';
import { BlueprintForm } from './BlueprintForm';
import { CategorySelector } from './CategorySelector';
import { FieldSelector } from './FieldSelector';
import { PromptEditor } from './PromptEditor';
import { TechnicalParamsEditor } from './TechnicalParams';
import { TechnicalParams, FieldValues, BlueprintData } from '../types';
import { IMAGE_GENERATION_MODELS, SCHEDULERS, PARAMETERS } from '@/lib/utils/constants/imageGeneration';
import { CARD_CATEGORIES } from '@/lib/utils/constants/cards';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useNavigate } from "react-router-dom";

interface BaseBlueprintBuilderProps {
  onTest: (result: any) => void;
  onSave: (result: any) => void;
  initialBlueprint?: BlueprintData;
}

// Add interface for test result
interface TestResult {
  prompt: string;
  imageUrl?: string;
  processingTime?: string;
}

export function BaseBlueprintBuilder({
  onTest,
  onSave,
  initialBlueprint: initialBlueprintProp
}: BaseBlueprintBuilderProps) {
  const { user } = useUser();
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [fieldValues, setFieldValues] = useState<FieldValues>({});
  const [template, setTemplate] = useState<string>('');
  const [result, setResult] = useState<TestResult | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaved, setIsSaved] = useState<boolean>(false);
  const [negativePrompt, setNegativePrompt] = useState<string>('');
  const [name, setName] = useState(initialBlueprintProp?.name || '');
  const [description, setDescription] = useState(initialBlueprintProp?.description || '');
  const [isPublic, setIsPublic] = useState(initialBlueprintProp?.visibility === 'public');
  const [isCollaborative, setIsCollaborative] = useState(initialBlueprintProp?.collaborative || false);
  const [params, setParams] = useState<TechnicalParams>({
    model: Object.keys(IMAGE_GENERATION_MODELS)[0],
    scheduler: Object.keys(SCHEDULERS)[0],
    guidanceScale: PARAMETERS.guidanceScale.default,
    inferenceSteps: PARAMETERS.inferenceSteps.default,
    seed: PARAMETERS.seed.default
  });
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [initialBlueprint, setInitialBlueprint] = useState(initialBlueprintProp);
  const [themeId, setThemeId] = useState<number>(initialBlueprintProp?.theme_id || 0);
  const [themes, setThemes] = useState<Array<{id: number, name: string}>>([]);
  const [userTier, setUserTier] = useState<string>('starter');
  const navigate = useNavigate();

  // Load initial data
  useEffect(() => {
    if (initialBlueprint) {
      console.log('Initial Blueprint:', initialBlueprint);
      console.log('Theme ID:', initialBlueprint.theme_id);
      setName(initialBlueprint.name);
      setDescription(initialBlueprint.description);
      setTemplate(initialBlueprint.configuration.creative.prompt);
      setNegativePrompt(initialBlueprint.configuration.creative.negative_prompt);
      setParams(initialBlueprint.configuration.technical);
      setThemeId(initialBlueprint.theme_id);
      
      // Extract category and fields
      const prompt = initialBlueprint.configuration.creative.prompt;
      const placeholders = prompt.match(/--([^-]+)--/g) || [];
      const fields = placeholders.map((p: string) => p.replace(/--/g, ''));
      
      // Find matching category based on theme_id
      const matchingCategory = Object.entries(CARD_CATEGORIES).find(([_, category]) => 
        category.themeId === initialBlueprint.theme_id
      );

      if (matchingCategory) {
        setSelectedCategory(matchingCategory[0]);
        const newFieldValues: FieldValues = {};
        fields.forEach((field: string) => {
          newFieldValues[field] = '';
        });
        setFieldValues(newFieldValues);
      }
    }
  }, [initialBlueprint]);

  // Modify the useEffect for category changes
  useEffect(() => {
    if (selectedCategory) {
      // Initialize field values with first option of each field
      const newFieldValues: FieldValues = {};
      Object.entries(CARD_CATEGORIES[selectedCategory].fields).forEach(([field, values]) => {
        if (values.length > 0) {
          const firstValue = values[0];
          newFieldValues[field] = typeof firstValue === 'string' ? firstValue : firstValue.name;
        }
      });
      setFieldValues(newFieldValues);
    } else {
      setFieldValues({});
    }
    setTemplate('');
    setResult(null);
  }, [selectedCategory]);

  // Update initialBlueprint when prop changes
  useEffect(() => {
    setInitialBlueprint(initialBlueprintProp);
  }, [initialBlueprintProp]);

  // Update useEffect for loading themes
  useEffect(() => {
    const loadThemes = async () => {
      try {
        const response = await listThemes();
        const themeData = response.data?.data || response.data || [];
        console.log('Theme data:', themeData);
        setThemes(Array.isArray(themeData) ? themeData : []);
      } catch (error) {
        console.error('Error loading themes:', error);
        setThemes([]);
      }
    };
    loadThemes();
  }, []);

  // Add this useEffect to check user tier
  useEffect(() => {
    const checkTier = async () => {
      const tier = await getUserTier();
      setUserTier(tier);
    };
    checkTier();
  }, []);

  const hasAccess = (tier: string): boolean => {
    return tier === 'admin' || tier === 'starter';
  };

  const generatePrompt = (): string => {
    let finalPrompt = template;
    Object.entries(fieldValues).forEach(([field, value]) => {
      const placeholder = `--${field}--`;
      finalPrompt = finalPrompt.replace(new RegExp(placeholder, 'g'), value);
    });
    return finalPrompt;
  };

  const isFormValid = (): boolean => {
    if (!initialBlueprint) {
      if (!name.trim() || !description.trim()) return false;
    }
    
    if (!selectedCategory) return false;
    
    const categoryFields = Object.keys(CARD_CATEGORIES[selectedCategory].fields);
    const hasAllFieldValues = categoryFields.every(field => fieldValues[field]);
    if (!hasAllFieldValues) return false;
    
    if (!template.trim() || !negativePrompt.trim()) return false;
    
    return true;
  };

  const getMissingFields = (): string[] => {
    const missing: string[] = [];

    if (!initialBlueprint) {
      if (!name.trim()) missing.push('Name');
      if (!description.trim()) missing.push('Description');
    }

    if (!selectedCategory) {
      missing.push('Category');
      return missing; // Return early since we need category first
    }

    // Check category fields
    const categoryFields = Object.keys(CARD_CATEGORIES[selectedCategory].fields);
    categoryFields.forEach(field => {
      if (!fieldValues[field]) {
        missing.push(field);
      }
    });

    // Check prompts
    if (!template.trim()) missing.push('Positive Prompt');
    if (!negativePrompt.trim()) missing.push('Negative Prompt');

    return missing;
  };

  const handleTest = async () => {
    if (!hasAccess(userTier)) {
      toast({
        variant: "destructive",
        title: "Feature not available",
        description: "Please upgrade to Starter tier to use this feature",
      });
      return;
    }
    setIsGenerating(true);
    const missingFields = getMissingFields();
    if (missingFields.length > 0) {
      toast({
        variant: "destructive",
        title: "Missing Required Fields",
        description: `Please fill in: ${missingFields.join(', ')}`,
      });
      return;
    }

    setIsLoading(true);
    try {
      const finalPrompt = generatePrompt();
      const requestData = {
        prompt: finalPrompt,
        negative_prompt: negativePrompt,
        params,
        userId: user?.id || '',
        username: user?.username || user?.firstName || "Unknown User"
      };

      // Call the API endpoint
      const response = await testBlueprint(requestData);
      const { imageUrl, processingTime } = response.data;
      
      console.log('Generation Parameters:', requestData);
      setResult({
        prompt: finalPrompt,
        imageUrl,
        processingTime
      });
      
      // Pass the complete result object to onTest
      onTest({
        ...requestData,
        imageUrl,
        processingTime
      });

      toast({
        title: "Success",
        description: "Prompt tested successfully",
      });
    } catch (error) {
      console.error('Error testing prompt:', error);
      setResult(null);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to test prompt",
      });
    } finally {
      setIsLoading(false);
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!hasAccess(userTier)) {
      toast({
        variant: "destructive",
        title: "Feature not available",
        description: "Please upgrade to Starter tier to use this feature",
      });
      return;
    }
    const missingFields = getMissingFields();
    if (missingFields.length > 0) {
      toast({
        variant: "destructive",
        title: "Missing Required Fields",
        description: `Please fill in: ${missingFields.join(', ')}`,
      });
      return;
    }

    try {
      const blueprint: CreateImageBlueprint = {
        name: name || `${selectedCategory} Template`,
        description: description,
        prompt: template,
        negativePrompt: negativePrompt,
        category: selectedCategory,
        theme_id: themeId,
        parameters: params,
      };

      console.log('Saving blueprint:', blueprint);

      if (initialBlueprint?.id) {
        const response = await updateBlueprint(Number(initialBlueprint.id), blueprint);
        // Update the initialBlueprint with the new version
        if (response?.data?.data) {
          setInitialBlueprint(prev => ({
            ...prev!,
            version: response.data.data.version
          }));
        }
      } else {
        await createBlueprint(blueprint);
      }
      
      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 3000);
      
      toast({
        title: "Success",
        description: `Blueprint ${initialBlueprint ? 'updated' : 'created'} successfully`,
      });

      onSave(blueprint);
    } catch (error) {
      console.error('Error saving blueprint:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to ${initialBlueprint ? 'update' : 'create'} blueprint`,
      });
    }
  };

  return (
    <div className="w-full">
      {userTier === 'free' && (
        <Alert className="mb-4 bg-muted">
          <AlertDescription className="flex items-center justify-between">
            <span>Upgrade to Starter tier to create and test blueprints</span>
            <Button 
              variant="default"
              onClick={() => navigate('/pricing')}
              className="ml-4"
            >
              Upgrade Now
            </Button>
          </AlertDescription>
        </Alert>
      )}
      <Card>
        <BuilderHeader
          initialBlueprint={initialBlueprint}
          isLoading={isLoading}
          isGenerating={isGenerating}
          isValid={isFormValid()}
          hasResult={!!result}
          isSaved={isSaved}
          onTest={handleTest}
          onSave={handleSave}
        />
        <CardContent className={cn(
          "transition-opacity duration-200",
          isLoading && "opacity-50 pointer-events-none"
        )}>
          <div className="space-y-4">
            <ScrollArea className="h-[calc(100vh-16rem)] pr-4">
              {!initialBlueprint && (
                <BlueprintForm
                  name={name}
                  description={description}
                  isPublic={isPublic}
                  isCollaborative={isCollaborative}
                  themeId={themeId}
                  themes={themes}
                  onNameChange={setName}
                  onDescriptionChange={setDescription}
                  onPublicChange={setIsPublic}
                  onCollaborativeChange={setIsCollaborative}
                  onThemeChange={setThemeId}
                />
              )}

              <CategorySelector
                selectedCategory={selectedCategory}
                onCategoryChange={setSelectedCategory}
                isEditing={!!initialBlueprint}
              />

              {selectedCategory && (
                <FieldSelector
                  selectedCategory={selectedCategory}
                  fieldValues={fieldValues}
                  onFieldValueChange={(field, value) => {
                    setFieldValues(prev => ({
                      ...prev,
                      [field]: value
                    }));
                  }}
                />
              )}

              <PromptEditor
                template={template}
                negativePrompt={negativePrompt}
                onTemplateChange={setTemplate}
                onNegativePromptChange={setNegativePrompt}
                generatedPrompt={result?.prompt || ''}
              />

              <TechnicalParamsEditor
                params={params}
                onParamsChange={setParams}
              />
            </ScrollArea>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 